import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  MapPin,
  Sparkles,
  Sun,
  ShieldCheck,
  HandCoins,
  ArrowRight,
  Users,
} from "lucide-react";
import { api } from "../lib/api";
import PropertyApplicationForm from "../components/PropertyApplicationForm";

const PILLARS = [
  {
    icon: Sparkles,
    title: "Curated demand",
    desc: "Sasha matches discerning Janté travelers — no bidding wars, no algorithmic noise.",
  },
  {
    icon: ShieldCheck,
    title: "Vetted guests",
    desc: "Clients come pre-screened through our concierge layer — every booking is filtered.",
  },
  {
    icon: HandCoins,
    title: "Premium rates",
    desc: "Janté guests come for the experience, not the discount. Charge what your space is worth.",
  },
  {
    icon: Sun,
    title: "Year-round bookings",
    desc: "Weather-led calendar matching surfaces your property the moment it shines.",
  },
];

const STEPS = [
  { n: "01", title: "Apply", desc: "Tell us about your property and the kind of guest you welcome." },
  { n: "02", title: "List", desc: "Add photography, amenities, and the moments that make your space yours." },
  { n: "03", title: "Sasha matches", desc: "When a Janté traveler's plans align, your stay is offered first." },
  { n: "04", title: "Host beautifully", desc: "We handle the concierge layer; you focus on the welcome." },
];

const FAQ = [
  {
    q: "What kinds of properties do you accept?",
    a: "Villas, penthouses, estates, chalets, beachfront retreats, boutique hotels, glass houses — anything that elevates the moment. We curate for character, not category.",
  },
  {
    q: "How are guests vetted?",
    a: "Every guest is a Janté member or has been routed by Sasha. We layer concierge oversight on every booking so what arrives at your door is intentional.",
  },
  {
    q: "What does the host fee structure look like?",
    a: "We work on a transparent net-of-fees model. After your application is accepted, we walk you through specifics tailored to your category and rate.",
  },
  {
    q: "How quickly can I start hosting?",
    a: "Most hosts are live within a week of approval — once your photography and amenities are loaded into the portal, Sasha can match you immediately.",
  },
];

export default function HostsPage() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/hosts");
        setHosts(r.data);
      } catch (e) {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="hosts-page">
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-16 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <div className="overline">
            <span className="text-[var(--brand-red)]">Janté Host Network</span>
            <span className="mx-2 opacity-60">·</span>
            For owners of remarkable spaces
          </div>
          <h1
            className="font-display text-5xl sm:text-6xl lg:text-7xl text-[var(--emerald)] leading-[1.05] mt-6"
            data-testid="hosts-headline"
          >
            Host the way<br />
            <em className="not-italic text-[var(--gold)]">Janté</em> travels.
          </h1>
          <p className="mt-7 text-lg leading-relaxed text-[var(--ink-2)] max-w-xl">
            A private network of villas, estates and singular stays — matched to travelers who arrive
            with intent and leave with stories. No noise, no race-to-the-bottom rates, no compromise.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <a
              href="#apply-form"
              className="btn-primary"
              data-testid="hosts-apply-btn"
            >
              Apply as a Host <ArrowRight size={14} strokeWidth={1.5} />
            </a>
            <a href="#how-it-works" className="btn-gold">
              How it works
            </a>
          </div>
        </div>

        <div className="lg:col-span-5 lux-card-dark" data-testid="hosts-sasha-card">
          <div className="flex items-start gap-4">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/sasha-avatar?v=2026-06-29a`}
              alt="Sasha — Janté concierge"
              className="w-20 h-20 rounded-full object-cover ring-2 ring-[var(--gold)]/40 shrink-0"
              data-testid="sasha-avatar-hosts"
            />
            <div>
              <div className="overline text-[var(--gold)]">Your concierge</div>
              <div className="font-display text-3xl mt-1" data-testid="sasha-name-hosts">Sasha</div>
              <p className="text-xs opacity-80 mt-1 italic" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                &ldquo;Tell me about your property &mdash; I&rsquo;ll set you up right.&rdquo;
              </p>
            </div>
          </div>
          <p className="text-sm opacity-90 mt-5">
            Sasha walks you through Bronze, Silver and Gold &mdash; explains the early-adopter window,
            the 20% commission, and which path (Tenant Thrive &middot; Property Assistance &middot; Rental-to-Rent)
            fits your property.
          </p>
          <Link to="/talk-to-sasha?topic=hosting" className="btn-gold mt-6 w-full" data-testid="hosts-talk-to-sasha">
            Chat with Sasha <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
          <Link to="/hosts/tiers" className="block text-center mt-2 text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] hover:opacity-80" data-testid="hosts-to-tiers">
            Or compare tiers →
          </Link>
          <div className="text-[10px] uppercase tracking-[0.2em] mt-3 opacity-70">
            Travelers, not hosting? <Link to="/stays" className="underline text-[var(--gold)]" data-testid="hosts-to-stays">Browse stays →</Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-[var(--ivory-2)] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="overline">Why host with us</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3 max-w-3xl">
            Demand that respects the space.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {PILLARS.map((p) => (
              <div key={p.title} className="lux-card">
                <p.icon size={22} strokeWidth={1.25} className="text-[var(--gold)]" />
                <h3 className="font-display text-2xl text-[var(--emerald)] mt-4">{p.title}</h3>
                <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs strip — links to the hidden detail pages */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="host-programs-strip">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="overline"><span className="text-[var(--brand-red)]">Early-adopter window</span> — 6 mo free by July 1, 2026 · 20% commission</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3">
              Three ways to grow with Janté.
            </h2>
            <p className="text-[var(--ink-2)] mt-3 max-w-xl text-sm">
              Tier up, hand the keys over, or lease the property to us entirely &mdash; all backed by Sasha&rsquo;s curated demand.
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <Link to="/hosts/tiers" className="lux-card transition-all hover:-translate-y-0.5" data-testid="program-card-tiers">
            <div className="overline"><span className="text-[var(--brand-red)]">Self-hosted</span></div>
            <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">Host Tiers</h3>
            <p className="text-sm text-[var(--ink-2)] mt-3">
              Bronze · Silver · Gold memberships. List on Janté, get page-positioning, Newsletter ads, dedicated concierge at Gold.
            </p>
            <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-[var(--emerald)]">6 months FREE (by Jul 1, 2026) · 20% commission →</div>
          </Link>
          <Link to="/hosts/property-assistance" className="lux-card transition-all hover:-translate-y-0.5" data-testid="program-card-pa">
            <div className="overline"><span className="text-[var(--brand-red)]">Hands-off</span></div>
            <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">JTL Property Assistance</h3>
            <p className="text-sm text-[var(--ink-2)] mt-3">
              Let Janté fully host or co-host your property. 10% of rental revenue — we take care of everything.
            </p>
            <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-[var(--emerald)]">Flat 10% · zero headaches →</div>
          </Link>
          <Link to="/hosts/rental-to-rent" className="lux-card transition-all hover:-translate-y-0.5" data-testid="program-card-r2r">
            <div className="overline"><span className="text-[var(--brand-red)]">Steady income</span></div>
            <h3 className="font-display text-3xl text-[var(--emerald)] mt-2">Rental-to-Rent</h3>
            <p className="text-sm text-[var(--ink-2)] mt-3">
              Janté leases your licensed STR directly and pays 1.5% of rental rates — predictable income, zero operational lift.
            </p>
            <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-[var(--emerald)]">1.5% rental pay-through →</div>
          </Link>
        </div>
        <p className="mt-8 text-[11px] uppercase tracking-[0.2em] text-[var(--brand-red)] flex items-center gap-2">
          <Sparkles size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
          Est. 20–30% lift in ratings, rates, and occupancy for Janté-listed properties.
        </p>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12">
          <div>
            <div className="overline">How it works</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3 leading-tight">
              From application to first guest, in days.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="border-l border-[var(--gold)]/40 pl-5">
                <div className="font-display text-3xl text-[var(--gold)]">{s.n}</div>
                <div className="font-display text-2xl text-[var(--emerald)] mt-2">{s.title}</div>
                <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form — Step 2 of the Hosts → Application → Next Steps flow */}
      <section
        id="apply-form"
        className="bg-[var(--ivory-2)] py-24 scroll-mt-20"
        data-testid="hosts-apply-section"
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <div className="overline text-[var(--brand-red)]">Hosts → Application → Next Steps</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3 leading-tight">
              Apply to host with Janté.
            </h2>
            <p className="text-[var(--ink-2)] mt-4 max-w-xl mx-auto text-sm">
              A few minutes now &mdash; a Property&rsquo;s Concierge will follow up within one week to review
              the next steps.
            </p>
          </div>
          <PropertyApplicationForm />
        </div>
      </section>

      {/* Hosts directory (Meet the Hosts) */}
      <section className="bg-[var(--ivory-2)] py-20" id="meet-hosts">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="overline">Meet the hosts</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3">
                The faces behind the keys.
              </h2>
              <p className="text-[var(--ink-2)] mt-3 max-w-xl text-sm">
                Each host on Janté is a curator of place — pick a host, see their collection.
              </p>
            </div>
            <Link to="/stays" className="btn-ghost" data-testid="hosts-meet-to-stays">
              Browse all properties <ArrowRight size={12} strokeWidth={1.5} />
            </Link>
          </div>

          {loading ? (
            <div className="text-[var(--muted)] py-12 text-center">Gathering our hosts…</div>
          ) : hosts.length === 0 ? (
            <div className="lux-card mt-10 text-center">
              <Building2 size={28} strokeWidth={1.25} className="text-[var(--gold)] mx-auto" />
              <h3 className="font-display text-2xl text-[var(--emerald)] mt-3">
                Our first hosts are coming online.
              </h3>
              <p className="text-sm text-[var(--ink-2)] mt-2">
                Be among the founding circle of the Janté Host Network.
              </p>
              <button
                onClick={() => {
                  document.getElementById("apply-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-primary mt-5"
                data-testid="hosts-empty-apply"
              >
                Apply as a Host
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {hosts.map((h, i) => (
                <Link
                  key={h.host_id}
                  to={`/hosts/${h.host_id}`}
                  className="lux-card flex flex-col cursor-pointer group"
                  data-testid={`host-card-${i}`}
                >
                  <div className="aspect-[16/10] -m-8 mb-4 overflow-hidden bg-[var(--ivory)]">
                    {h.cover_image ? (
                      <img
                        src={h.cover_image}
                        alt={h.host_name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--muted)]">
                        <Building2 size={28} strokeWidth={1.25} />
                      </div>
                    )}
                  </div>
                  <div className="overline flex items-center gap-1.5">
                    <Users size={12} strokeWidth={1.25} /> Janté Host
                  </div>
                  <h3 className="font-display text-3xl text-[var(--emerald)] mt-1">{h.host_name}</h3>
                  <div className="text-xs text-[var(--ink-2)] mt-2 flex items-center gap-1.5">
                    <MapPin size={12} strokeWidth={1.25} className="text-[var(--gold)]" />
                    {(h.locations || []).slice(0, 3).join(" · ")}
                  </div>
                  {h.highlights && h.highlights.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {h.highlights.slice(0, 3).map((tag) => (
                        <span
                          key={`${h.id || h.host_id}-${tag}`}
                          className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-red)] border border-[var(--gold)]/40 px-2 py-0.5"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-5 flex items-baseline justify-between border-t border-[var(--emerald)]/10 pt-4">
                    <div>
                      <div className="font-display text-2xl text-[var(--emerald)] tabular-nums">
                        {h.listings}{" "}
                        <span className="text-sm uppercase tracking-[0.18em] text-[var(--ink-2)]">
                          listing{h.listings === 1 ? "" : "s"}
                        </span>
                      </div>
                      {h.min_rate ? (
                        <div className="text-xs text-[var(--ink-2)] mt-1">
                          From ${Math.round(h.min_rate).toLocaleString()} / night
                        </div>
                      ) : null}
                    </div>
                    <span className="btn-gold !py-2 !px-3 text-[10px]">View host</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24">
        <div className="overline text-center">Common questions</div>
        <h2 className="font-display text-4xl sm:text-5xl text-[var(--emerald)] mt-3 text-center">
          Hosting, answered.
        </h2>
        <div className="mt-12 divide-y divide-[var(--emerald)]/10">
          {FAQ.map((f) => (
            <details key={f.q} className="py-5 group" data-testid={`hosts-faq-${f.q.slice(0,12).replace(/\s+/g,"-").toLowerCase()}`}>
              <summary className="cursor-pointer flex items-center justify-between gap-6 list-none">
                <span className="font-display text-2xl text-[var(--emerald)]">{f.q}</span>
                <span className="text-[var(--gold)] transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
              </summary>
              <p className="text-[var(--ink-2)] mt-3 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--ivory-2)] py-20" data-testid="hosts-final-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Sparkles size={20} strokeWidth={1.25} className="text-[var(--gold)] mx-auto" />
          <h2 className="font-display text-4xl sm:text-5xl mt-5 text-[var(--emerald)]">
            Your space, on the Janté network.
          </h2>
          <p className="text-[var(--ink-2)] mt-4 max-w-xl mx-auto">
            We&rsquo;re building the most considered short-term rental network in the world &mdash; one host at a time.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <a href="#apply-form" className="btn-primary" data-testid="hosts-cta-apply">
              Apply as a Host
            </a>
            <Link to="/host" className="btn-ghost" data-testid="hosts-cta-portal">
              Open host portal
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
